import React, { useCallback, useEffect, useState } from "react";
import { api, inr, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, ShoppingCart, Trash2, User as UserIcon, Ghost, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 15;

export default function AdminCarts() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [hasUser, setHasUser] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (q) params.q = q;
      if (hasUser !== "all") params.has_user = hasUser;
      const [rowsRes, sumRes] = await Promise.all([
        api.get("/admin/carts", { params }),
        api.get("/admin/carts/summary"),
      ]);
      setRows(rowsRes.data.items);
      setTotal(rowsRes.data.total);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error("Failed to load carts");
    } finally { setLoading(false); }
  }, [page, q, hasUser]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Remove this cart?")) return;
    await api.delete(`/admin/carts/${id}`);
    load();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2">
          <ShoppingCart className="w-6 h-6 text-hyd-gold" /> Pending carts
        </h1>
        <span className="text-hyd-text2 text-sm">{summary?.total ?? 0} live</span>
      </div>
      <p className="text-hyd-text2 text-sm">Every cart with 1+ item is tracked here — logged-in users show name & phone, anonymous carts show session only.</p>

      {/* Summary chips */}
      {summary && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total carts" value={summary.total} accent="text-hyd-gold" />
          <Stat label="Logged-in" value={summary.with_user} accent="text-hyd-red" icon={UserIcon} />
          <Stat label="Anonymous" value={summary.anonymous} accent="text-hyd-text2" icon={Ghost} />
          <Stat label="Cart value" value={inr(summary.value)} accent="text-hyd-gold" />
        </div>
      )}

      {/* Filters */}
      <div className="mt-4 flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
        <div className="flex-1">
          <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest mb-1 flex items-center gap-1">
            <Search className="w-3 h-3 text-hyd-gold" /> Search phone / name / product
          </Label>
          <Input
            data-testid="admin-carts-search"
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
            className="bg-hyd-panel border-hyd-border rounded-xl h-10"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest mb-1 block">Type</Label>
          <Select value={hasUser} onValueChange={(v) => { setPage(1); setHasUser(v); }}>
            <SelectTrigger data-testid="admin-carts-type" className="bg-hyd-panel border-hyd-border rounded-xl h-10 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="yes">Logged-in only</SelectItem>
              <SelectItem value="no">Anonymous only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 bg-hyd-panel border border-hyd-border rounded-3xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-hyd-text2">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-hyd-text3">No pending carts.</div>
        ) : (
          <div className="divide-y divide-hyd-border">
            {rows.map((c) => {
              const open = expanded === c.id;
              return (
                <div key={c.id} data-testid={`admin-cart-row-${c.id}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded(open ? null : c.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(open ? null : c.id); } }}
                    className="w-full text-left px-4 py-3 hover:bg-hyd-panel2/60 flex items-center gap-3 flex-wrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-hyd-gold/40"
                  >
                    <div className="min-w-[180px]">
                      {c.user_id ? (
                        <>
                          <div className="text-sm font-semibold flex items-center gap-1"><UserIcon className="w-3.5 h-3.5 text-hyd-gold" /> {c.user_name}</div>
                          <div className="text-xs text-hyd-text3">{c.user_phone}</div>
                        </>
                      ) : (
                        <div className="text-sm text-hyd-text2 flex items-center gap-1"><Ghost className="w-3.5 h-3.5" /> Anonymous</div>
                      )}
                      <div className="text-[10px] text-hyd-text3 mt-0.5 font-mono truncate max-w-[180px]">{c.session_id}</div>
                    </div>
                    <div className="text-xs text-hyd-text2 hidden md:block min-w-[80px]">{c.total_qty} items</div>
                    <div className="text-hyd-gold font-bold text-sm min-w-[80px] text-right">{inr(c.subtotal)}</div>
                    <div className="text-[10px] text-hyd-text3 hidden md:block min-w-[140px]">
                      Updated {new Date(c.updated_at).toLocaleString()}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); del(c.id); }} className="text-hyd-red hover:bg-hyd-panel">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                      {open ? <ChevronUp className="w-4 h-4 text-hyd-text2" /> : <ChevronDown className="w-4 h-4 text-hyd-text2" />}
                    </div>
                  </div>
                  {open && (
                    <div data-testid={`admin-cart-detail-${c.id}`} className="bg-hyd-bg/60 border-t border-hyd-border px-4 py-4">
                      <div className="bg-hyd-panel border border-hyd-border rounded-2xl divide-y divide-hyd-border">
                        {c.items.map((i, idx) => (
                          <div key={idx} className="p-3 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-hyd-panel2 overflow-hidden shrink-0 border border-hyd-border">
                              {i.image_url && <img src={imageUrl(i.image_url)} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold line-clamp-2">{i.name}</div>
                              <div className="text-xs text-hyd-text3">{i.qty} × {inr(i.unit_price)} · {i.kind}</div>
                            </div>
                            <div className="text-hyd-gold font-bold text-sm">{inr(i.qty * i.unit_price)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-hyd-text2">
          Showing {rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{(page - 1) * PAGE_SIZE + rows.length} of {total}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <div className="text-xs text-hyd-text2 min-w-[80px] text-center">Page {page} of {totalPages}</div>
          <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-hyd-gold", icon: Icon }) {
  return (
    <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-4">
      <div className="text-[10px] uppercase text-hyd-text2 tracking-widest flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3 text-hyd-gold" />} {label}
      </div>
      <div className={`font-display text-2xl font-bold mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
